/**
 * DeviceSettings — single-column property inspector covering all six
 * configurable shadow categories (audio / security / usb / device_info
 * / debug / features, spec §9.3).
 *
 * Replaces the previous 6-card grid + disabled-placeholder UX. Each
 * row is now read-first, edit-on-demand via SettingsRow; mutations go
 * through the `useSetDeviceSettings` hook (currently a mock — the
 * real PATCH endpoint lands in a follow-up).
 *
 * Hardware-gated fields (descriptor.hw_models doesn't include the
 * device's hw_ver) are simply omitted, not greyed out — show only
 * what's actually settable.
 *
 * A live search input filters category labels and field labels in
 * real time. Category sections with no matching rows hide entirely.
 */
import { useState } from 'react';
import {
  useDeviceSettings,
  useDeviceSettingsState,
  useSettingsRejections,
  type HwModel,
  type SettingDescriptor,
  type SettingsCategory,
} from '../api/hooks';
import { SettingsRow } from './SettingsRow';

const CATEGORY_LABEL: Record<SettingsCategory, string> = {
  audio: 'Audio',
  security: 'Security',
  usb: 'USB',
  device_info: 'Device info',
  debug: 'Debug',
  features: 'Features',
  uc_logs: 'uC log storage',
};

const CATEGORY_ORDER: SettingsCategory[] = [
  'audio',
  'security',
  'usb',
  'device_info',
  // Debug is what the uC SENDS; uC log storage is what the gateway KEEPS.
  // Adjacent on purpose — they are read together.
  'debug',
  'uc_logs',
  'features',
];

/** Store depth, rendered as prose under the uC log storage header.
 *
 * Deliberately NOT catalog rows: the values are nested objects
 * ({bytes, oldest_recv}) rather than settings, and a descriptor typed `text`
 * renders them as "[object Object]". A settings catalog is for things you can
 * set. */
function formatStoreBytes(n: unknown): string | null {
  if (typeof n !== 'number') return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTier(tier: unknown): string | null {
  if (!tier || typeof tier !== 'object') return null;
  const t = tier as { bytes?: unknown; oldest_recv?: unknown };
  const size = formatStoreBytes(t.bytes);
  if (size === null) return null;
  if (typeof t.oldest_recv !== 'string') return size;
  // Where the history starts is the useful part: it answers "is a bundle
  // worth requesting for a fault last Tuesday?"
  return `${size} back to ${new Date(t.oldest_recv).toLocaleString()}`;
}

function storeDepthLine(ucLogs: unknown): string | null {
  if (!ucLogs || typeof ucLogs !== 'object') return null;
  const u = ucLogs as Record<string, unknown>;
  const ram = formatTier(u.volatile);
  const flash = formatTier(u.durable);
  if (!ram && !flash) return null;
  const parts = [
    ram ? `RAM ${ram} (lost on reboot)` : null,
    flash ? `flash ${flash}` : null,
  ].filter(Boolean);
  const dropped =
    Number(u.dropped_queue ?? 0) + Number(u.dropped_no_space ?? 0);
  if (dropped > 0) parts.push(`${dropped} dropped`);
  return parts.join(' · ');
}

function fieldVisible(d: SettingDescriptor, hw: string | undefined): boolean {
  if (!d.hw_models || d.hw_models.length === 0) return true;
  if (!hw) return true; // unknown hw — show everything (conservative)
  return d.hw_models.includes(hw as HwModel);
}

function humaniseKey(k: string): string {
  return k
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (m) => m.toUpperCase());
}

function descriptorMatchesSearch(
  d: SettingDescriptor,
  cat: SettingsCategory,
  q: string,
): boolean {
  if (!q) return true;
  const hay =
    `${cat} ${CATEGORY_LABEL[cat]} ${d.key} ${humaniseKey(d.key)} ${d.description ?? ''}`.toLowerCase();
  return hay.includes(q.toLowerCase());
}

export function DeviceSettings({
  thing,
  hw,
}: {
  thing: string;
  hw: string | undefined;
}) {
  const q = useDeviceSettings(thing);
  // Live shadow halves from the admin Lambda. The catalog query above still
  // serves the field definitions (and a cached `reported` for a device whose
  // live read is unavailable), but desired only exists here.
  const live = useDeviceSettingsState(thing);
  const rejections = useSettingsRejections(thing);
  const [search, setSearch] = useState('');
  // Categories collapsed by default — operator goes straight to the
  // section they care about rather than scrolling past 40+ rows. An
  // active search force-expands anything that matches so users don't
  // have to click first to discover results.
  const [collapsed, setCollapsed] = useState<Record<SettingsCategory, boolean>>(
    {
      audio: true,
      security: true,
      usb: true,
      device_info: true,
      debug: true,
      uc_logs: true,
      features: true,
    },
  );
  const toggleCat = (cat: SettingsCategory) =>
    setCollapsed((s) => ({ ...s, [cat]: !s[cat] }));

  if (q.isLoading) {
    return (
      <section className="card">
        <h2 className="text-lg font-semibold tracking-tight mb-3">Settings</h2>
        <p className="text-sm text-muted">Loading settings…</p>
      </section>
    );
  }
  if (q.error) {
    return (
      <section className="card">
        <h2 className="text-lg font-semibold tracking-tight mb-3">Settings</h2>
        <p className="text-sm text-warn">{(q.error as Error).message}</p>
      </section>
    );
  }
  if (!q.data) return null;

  // Snapshot the filtered field count per category so the search
  // empty-state knows whether anything matches anywhere.
  const filteredByCat: Array<{
    cat: SettingsCategory;
    fields: SettingDescriptor[];
  }> = CATEGORY_ORDER.map((cat) => {
    const catalog = q.data!.catalog[cat] ?? [];
    const fields = catalog
      .filter((d) => fieldVisible(d, hw))
      .filter((d) => descriptorMatchesSearch(d, cat, search));
    return { cat, fields };
  });

  // Two floors, and the lower one can only ever see what the upper one lets
  // through: asking to keep info-level records on flash does nothing while the
  // uC is still only sending warnings. Say so rather than let the operator
  // wonder why nothing was recorded.
  const ucFloor = Number(
    (live.data?.desired.debug?.min_level ?? live.data?.reported.debug?.min_level) ??
      NaN,
  );
  const flashFloor = Number(
    (live.data?.desired.uc_logs?.persist_level ??
      live.data?.reported.uc_logs?.persist_level) ?? NaN,
  );
  const depthLine = storeDepthLine(live.data?.reported.uc_logs);
  // A desired key the device never applies keeps AWS re-sending the shadow
  // delta on every reported update — uplink traffic on a metered SIM for as
  // long as it sits there. uc-bridge stops retrying a permanently refused key,
  // but only clearing `desired` stops the delta, and that is an operator action.
  const stuck = live.data?.stuck ?? [];
  const floorHint =
    Number.isFinite(ucFloor) && Number.isFinite(flashFloor) && flashFloor < ucFloor
      ? `The uC is only sending level ${ucFloor} and above, so the flash floor of ${flashFloor} has nothing extra to catch yet — lower “Debug → min level” too.`
      : null;

  const totalMatches = filteredByCat.reduce(
    (n, { fields }) => n + fields.length,
    0,
  );
  const allCategoriesNull = CATEGORY_ORDER.every(
    (c) => q.data!.reported[c] === null,
  );

  return (
    <section className="card">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <h2 className="text-lg font-semibold tracking-tight">Settings</h2>
        <div className="flex items-center gap-2">
          <input
            type="search"
            placeholder="Search settings…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1.5 text-sm border border-border rounded-md w-56 focus:outline-none focus:border-accent"
          />
          <button
            type="button"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
            className="px-2 py-1.5 rounded-md text-sm border border-border bg-white text-muted hover:text-fg disabled:opacity-50 leading-none"
            title={q.isFetching ? 'Refreshing…' : 'Refresh now'}
            aria-label="Refresh settings"
          >
            <span
              className={`inline-block ${q.isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            >
              ↻
            </span>
          </button>
        </div>
      </div>

      {floorHint && <p className="text-[11px] text-warn mb-3">{floorHint}</p>}

      {stuck.length > 0 && (
        <div className="mb-3 rounded-md border border-warn/40 bg-warn/5 px-3 py-2">
          <p className="text-[11px] text-warn font-medium">
            {stuck.length === 1 ? '1 setting is' : `${stuck.length} settings are`}{' '}
            not being applied by this device
          </p>
          <ul className="mt-1 space-y-0.5">
            {stuck.map((sk) => (
              <li key={sk.key} className="text-[11px] text-muted font-mono">
                {sk.key}: asked for {JSON.stringify(sk.desired)}, device reports{' '}
                {JSON.stringify(sk.reported)}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-[10px] text-muted">
            While this persists the device is re-sent the same request on every
            state report, which costs cellular data. If the device cannot apply
            it, clear the key from the shadow&rsquo;s desired half.
          </p>
        </div>
      )}

      {allCategoriesNull && (
        <p className="text-sm text-muted mb-4">
          uC has not reported any settings yet. When uc-bridge is alive on this
          device, the categories below populate automatically.
        </p>
      )}

      {search && totalMatches === 0 && (
        <p className="text-sm text-muted">
          No settings match &ldquo;{search}&rdquo;.
        </p>
      )}

      <div className="space-y-3">
        {filteredByCat.map(({ cat, fields }) => {
          if (fields.length === 0) return null;
          // Prefer the live shadow; fall back to the RDS mirror while the
          // admin call is in flight or forbidden for this role.
          const reported = live.data?.reported[cat] ?? q.data!.reported[cat];
          const desired = live.data?.desired[cat];
          // Active search force-expands so matches don't hide.
          const isCollapsed = search ? false : collapsed[cat];
          return (
            <div key={cat}>
              <button
                type="button"
                onClick={() => toggleCat(cat)}
                className="w-full flex items-baseline gap-2 text-left pb-1 border-b border-border mb-1 hover:text-fg group"
                aria-expanded={!isCollapsed}
                title={
                  isCollapsed ? `Expand ${CATEGORY_LABEL[cat]}` : `Collapse ${CATEGORY_LABEL[cat]}`
                }
              >
                <span
                  aria-hidden="true"
                  className="inline-block w-3 text-center text-[10px] text-muted group-hover:text-fg"
                >
                  {isCollapsed ? '▸' : '▾'}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-muted group-hover:text-fg">
                  {CATEGORY_LABEL[cat]}
                </span>
                <span className="ml-1 text-[10px] text-muted tabular-nums">
                  {fields.length}
                </span>
                {reported === null && (
                  <span className="ml-2 text-warn normal-case tracking-normal text-[11px]">
                    (not reported yet)
                  </span>
                )}
              </button>
              {!isCollapsed && cat === 'uc_logs' && depthLine && (
                <p className="text-[11px] text-muted pb-1">{depthLine}</p>
              )}
              {!isCollapsed && (
                <div>
                  {fields.map((d) => (
                    <SettingsRow
                      key={`${cat}.${d.key}`}
                      thing={thing}
                      category={cat}
                      descriptor={d}
                      reportedValue={
                        reported
                          ? (reported as Record<string, unknown>)[d.key]
                          : undefined
                      }
                      desiredValue={
                        desired
                          ? (desired as Record<string, unknown>)[d.key]
                          : undefined
                      }
                      rejection={rejections.get(`${cat}.${d.key}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
